import { useState, useMemo } from 'react'
import {
  Zap, Upload, Play, Pause, Square, Trash2, Plus,
  Check, X, Clock, Phone, RotateCcw, FileText
} from 'lucide-react'
import useCampaignStore from '../stores/campaignStore'
import useSipStore from '../stores/sipStore'

function parseNumbers(text) {
  // Accept CSV, comma-separated, newline-separated, or whitespace.
  // Strip everything that isn't 0-9 + - * # @ . (so country codes survive),
  // then filter to runs of digits >= 7.
  return text
    .split(/[\s,;]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0)
    .map(s => s.replace(/[^\d+#*]/g, ''))
    .filter(s => s.replace(/[^\d]/g, '').length >= 7)
}

const STATUS_STYLE = {
  pending:   { cls: 'text-mute', icon: Clock,  label: 'ממתין' },
  dialing:   { cls: 'text-yellow-300 animate-pulse', icon: Phone, label: 'מחייג' },
  answered:  { cls: 'text-emerald-300', icon: Phone, label: 'בשיחה' },
  done:      { cls: 'text-emerald-400', icon: Check, label: 'בוצע' },
  failed:    { cls: 'text-rose-400', icon: X, label: 'נכשל' },
}

const MODES = [
  { value: 'preview',    label: 'Preview',    desc: 'חיוג ידני - מאשרים כל מספר' },
  { value: 'power',      label: 'Power',      desc: 'אוטומטי - אחד אחרי השני' },
  { value: 'predictive', label: 'Predictive', desc: 'מקבילי - N שיחות יחד' },
]

function NewCampaignDialog({ onClose, onCreate }) {
  const [name, setName] = useState('')
  const [text, setText] = useState('')
  const [mode, setMode] = useState('power')

  const numbers = useMemo(() => parseNumbers(text), [text])

  const handleFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setText(String(reader.result || ''))
    reader.readAsText(file)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-panel border border-line rounded-2xl p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2">
            <Zap size={18} className="text-brand" />
            קמפיין חדש
          </h2>
          <button onClick={onClose} className="text-mute hover:text-text">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-mute text-xs mb-1">שם הקמפיין</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="לדוגמא: לידים אוגוסט"
              className="w-full px-3 py-2 bg-bg border border-line rounded-lg"
            />
          </div>

          <div>
            <label className="block text-mute text-xs mb-1">מצב חיוג</label>
            <div className="grid grid-cols-3 gap-2">
              {MODES.map(m => (
                <button
                  key={m.value}
                  onClick={() => setMode(m.value)}
                  className={`p-2 rounded-lg border text-right transition-colors
                    ${mode === m.value
                      ? 'bg-brand/20 border-brand/40 text-text'
                      : 'border-line hover:bg-line/30 text-mute'
                    }`}
                >
                  <div className="font-medium text-sm">{m.label}</div>
                  <div className="text-[10px] opacity-75 leading-tight">{m.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-mute text-xs">מספרים</label>
              <label className="flex items-center gap-1 text-xs text-brand cursor-pointer hover:underline">
                <Upload size={12} />
                העלה CSV / TXT
                <input type="file" accept=".csv,.txt" onChange={handleFile} className="hidden" />
              </label>
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="0501234567&#10;0541234567&#10;..."
              rows={8}
              className="w-full px-3 py-2 bg-bg border border-line rounded-lg font-mono text-xs ltr"
            />
            <div className="text-xs text-mute mt-1 flex justify-between">
              <span>{numbers.length} מספרים תקינים</span>
              {numbers.length > 0 && (
                <span className="ltr tech opacity-75">
                  {numbers.slice(0, 3).join(', ')}{numbers.length > 3 ? '…' : ''}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-line hover:bg-line/30 text-sm"
          >
            ביטול
          </button>
          <button
            onClick={() => { onCreate(name, mode, numbers); onClose() }}
            disabled={numbers.length === 0}
            className="flex-1 py-2 rounded-lg bg-brand hover:bg-brand/90 disabled:bg-line
              text-slate-900 font-medium text-sm disabled:text-mute"
          >
            צור קמפיין ({numbers.length})
          </button>
        </div>
      </div>
    </div>
  )
}

function CampaignDetail({ campaign, onClose }) {
  const start = useCampaignStore(s => s.start)
  const pause = useCampaignStore(s => s.pause)
  const resume = useCampaignStore(s => s.resume)
  const stop = useCampaignStore(s => s.stop)
  const dialNext = useCampaignStore(s => s.dialNext)
  const resetCampaign = useCampaignStore(s => s.resetCampaign)
  const update = useCampaignStore(s => s.update)
  const updateNumber = useCampaignStore(s => s.updateNumber)
  const deleteCmp = useCampaignStore(s => s.delete)

  const stats = useMemo(() => {
    const s = { pending: 0, dialing: 0, answered: 0, done: 0, failed: 0 }
    for (const n of campaign.numbers) s[n.status] = (s[n.status] || 0) + 1
    s.total = campaign.numbers.length
    s.completed = s.done + s.failed
    s.percentage = Math.round((s.completed / s.total) * 100) || 0
    return s
  }, [campaign])

  const isRunning = campaign.status === 'running'
  const isDone = stats.completed === stats.total

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-panel border border-line rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-line flex items-center justify-between shrink-0">
          <div>
            <h2 className="font-semibold">{campaign.name}</h2>
            <div className="text-xs text-mute flex items-center gap-2 mt-0.5">
              <span className="px-1.5 py-0.5 rounded bg-line/40">{
                MODES.find(m => m.value === campaign.mode)?.label || campaign.mode
              }</span>
              <span>{stats.total} מספרים</span>
              {campaign.status === 'done' && <span className="text-emerald-400">✓ הסתיים</span>}
              {campaign.status === 'paused' && <span className="text-yellow-400">⏸ מושהה</span>}
              {campaign.status === 'running' && <span className="text-emerald-400 animate-pulse">▶ פעיל</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-mute hover:text-text">
            <X size={20} />
          </button>
        </div>

        {/* Progress + controls */}
        <div className="p-4 border-b border-line shrink-0 space-y-3">
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-mute">התקדמות</span>
              <span>{stats.completed}/{stats.total} ({stats.percentage}%)</span>
            </div>
            <div className="h-2 bg-line/40 rounded-full overflow-hidden">
              <div className="h-full bg-brand transition-all duration-300" style={{ width: `${stats.percentage}%` }} />
            </div>
            <div className="flex justify-between text-[11px] text-mute pt-1">
              <span className="text-emerald-400">✓ {stats.done}</span>
              <span className="text-rose-400">✗ {stats.failed}</span>
              {stats.dialing + stats.answered > 0 && (
                <span className="text-yellow-300">▶ {stats.dialing + stats.answered}</span>
              )}
              <span>⏳ {stats.pending}</span>
            </div>
          </div>

          {campaign.mode === 'predictive' && !isRunning && (
            <div>
              <label className="text-xs text-mute">שיחות מקבילות: {campaign.concurrency}</label>
              <input
                type="range"
                min="1"
                max="10"
                value={campaign.concurrency}
                onChange={(e) => update(campaign.id, { concurrency: parseInt(e.target.value) })}
                className="w-full mt-1"
              />
            </div>
          )}

          <div className="flex gap-2">
            {campaign.mode === 'preview' ? (
              <button
                onClick={() => dialNext(campaign.id)}
                disabled={stats.pending === 0}
                className="flex-1 py-2 rounded-lg bg-brand hover:bg-brand/90 disabled:bg-line
                  text-slate-900 font-medium text-sm flex items-center justify-center gap-2"
              >
                <Phone size={14} /> חייג למספר הבא
              </button>
            ) : isDone ? (
              <button
                onClick={() => resetCampaign(campaign.id)}
                className="flex-1 py-2 rounded-lg bg-line/40 hover:bg-line/60 text-sm
                  flex items-center justify-center gap-2"
              >
                <RotateCcw size={14} /> אפס והתחל מחדש
              </button>
            ) : !isRunning ? (
              <button
                onClick={() => (campaign.status === 'paused' ? resume : start)(campaign.id)}
                className="flex-1 py-2 rounded-lg bg-brand hover:bg-brand/90 text-slate-900 font-medium text-sm
                  flex items-center justify-center gap-2"
              >
                <Play size={14} /> {campaign.status === 'paused' ? 'המשך' : 'התחל'}
              </button>
            ) : (
              <button
                onClick={() => pause(campaign.id)}
                className="flex-1 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-medium text-sm
                  flex items-center justify-center gap-2"
              >
                <Pause size={14} /> השהה
              </button>
            )}

            {isRunning && (
              <button
                onClick={stop}
                className="px-4 py-2 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 text-sm
                  flex items-center justify-center gap-1"
              >
                <Square size={12} />
              </button>
            )}

            <button
              onClick={() => { if (confirm('למחוק קמפיין?')) { deleteCmp(campaign.id); onClose() } }}
              className="px-3 py-2 rounded-lg border border-line hover:bg-line/30 text-mute hover:text-rose-400"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {/* Number list */}
        <div className="flex-1 overflow-y-auto p-2">
          <div className="space-y-0.5">
            {campaign.numbers.map((n, i) => {
              const st = STATUS_STYLE[n.status] || STATUS_STYLE.pending
              const Icon = st.icon
              return (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-line/20 text-sm">
                  <span className="text-mute text-xs w-7 tech ltr text-end">{i + 1}.</span>
                  <Icon size={14} className={st.cls + ' shrink-0'} />
                  <span className="font-mono ltr tech flex-1 min-w-0 truncate">{n.raw}</span>
                  {n.durationSec ? <span className="text-xs text-mute tech ltr">{n.durationSec}s</span> : null}
                  <span className={`text-xs ${st.cls} w-12`}>{st.label}</span>
                  {(n.status === 'done' || n.status === 'failed') && (
                    <input
                      type="text"
                      placeholder="הערה"
                      value={n.notes || ''}
                      onChange={(e) => updateNumber(campaign.id, i, { notes: e.target.value })}
                      className="px-2 py-0.5 bg-bg border border-line/40 rounded text-xs w-32"
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function PowerDialer() {
  const campaigns = useCampaignStore(s => s.campaigns)
  const create = useCampaignStore(s => s.create)
  const [showNew, setShowNew] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  const selected = campaigns.find(c => c.id === selectedId)

  return (
    <div className="bg-panel border border-line rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-text flex items-center gap-2">
          <Zap size={16} className="text-brand" />
          תותח שיחות <span className="text-xs text-mute">({campaigns.length})</span>
        </h2>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-brand hover:bg-brand/90 text-slate-900 text-xs font-medium"
        >
          <Plus size={12} />
          חדש
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center text-mute py-8">
          <FileText size={28} className="mx-auto mb-2 opacity-40" />
          <div className="text-sm">אין קמפיינים</div>
          <div className="text-xs mt-1">לחץ "חדש" כדי להתחיל</div>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-96 overflow-y-auto">
          {campaigns.map(c => {
            const done = c.numbers.filter(n => n.status === 'done' || n.status === 'failed').length
            const pct = Math.round((done / c.numbers.length) * 100) || 0
            return (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className="w-full text-right p-2 rounded-lg hover:bg-line/30 border border-line/40 transition-colors"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-sm truncate">{c.name}</div>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    c.status === 'running' ? 'bg-emerald-500/20 text-emerald-300 animate-pulse' :
                    c.status === 'paused' ? 'bg-yellow-500/20 text-yellow-300' :
                    c.status === 'done' ? 'bg-line/40 text-mute' :
                    'bg-line/40 text-mute'
                  }`}>
                    {c.status === 'running' ? 'פעיל' :
                     c.status === 'paused' ? 'מושהה' :
                     c.status === 'done' ? 'הסתיים' : 'ממתין'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-line/40 rounded-full overflow-hidden">
                    <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-mute tech w-16 text-end">{done}/{c.numbers.length}</span>
                </div>
              </button>
            )
          })}
        </div>
      )}

      {showNew && (
        <NewCampaignDialog
          onClose={() => setShowNew(false)}
          onCreate={(name, mode, numbers) => {
            const id = create(name, mode, numbers)
            setSelectedId(id)
          }}
        />
      )}

      {selected && (
        <CampaignDetail
          campaign={selected}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  )
}
